import {
  maybeParseCalendarDate,
  parseZonedDateTime,
} from '@rematter/common/dateTime';
import {
  BillSortOption,
  BillStatus,
  BillType,
} from '@rematter/frontend-lib-api-client';

import { BillRecord } from '../types';

import { toMoney } from '~/modules/trade/utils/money';
import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const billsQueryAtom = createQueryAtomFactory(
  'finance.bills',
  (params: {
    searchTerm?: string;
    statuses?: BillStatus[];
    types?: BillType[];
    accountIDs?: string[];
    facilityIDs?: string[];
    limit?: number;
    offset?: number;
    sorting: BillSortOption[];
  }) => ({
    bills: {
      __args: {
        filter: {
          searchTerm: params.searchTerm,
          statuses: params.statuses,
          types: params.types,
          accountIDs: params.accountIDs,
          facilityIDs: params.facilityIDs,
        },
        pagination: {
          offset: params.offset ?? 0,
          limit: params.limit ?? 10,
        },
        sorting: params.sorting,
      },
      entries: {
        id: true,
        type: true,
        documentNumber: true,
        documentDate: true,
        eligibleAt: true,
        dueDate: true,
        status: true,
        notes: true,
        paymentTerm: true,
        preferredPaymentMethod: true,
        compliance: true,
        scaleTicketId: true,
        account: {
          id: true,
          name: true,
          type: true,
        },
        facility: {
          id: true,
          facilityName: true,
        },
        amount: {
          amount: true,
          currencyCode: true,
        },
        openAmount: {
          amount: true,
          currencyCode: true,
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): { bills: BillRecord[]; total?: number } => {
      if (!data?.bills.entries.length) {
        return { bills: [], total: 0 };
      }

      return {
        bills: data?.bills.entries.map((it) => ({
          id: it.id,
          type: it.type,
          documentNumber: it.documentNumber,
          documentDate: maybeParseCalendarDate(it.documentDate) ?? null,
          eligibleAt: maybeParseCalendarDate(it.eligibleAt) ?? null,
          dueDate: maybeParseCalendarDate(it.dueDate) ?? null,
          accountId: it.account?.id ?? null,
          accountName: it.account?.name ?? '',
          accountType: it.account?.type,
          facilityId: it.facility?.id ?? '',
          facilityName: it.facility?.facilityName ?? '',
          amount: toMoney(it.amount),
          openAmount: toMoney(it.openAmount),
          compliance: it.compliance,
          status: it.status,
          notes: it.notes ?? null,
          paymentTerm: it.paymentTerm ?? null,
          preferredPaymentMethod: it.preferredPaymentMethod ?? null,
          scaleTicketId: it.scaleTicketId ?? null,
          lineItems: [],
          payments: [],
          timestamps: {
            createdAt: parseZonedDateTime(it.timestamps.createdAt),
            updatedAt: parseZonedDateTime(it.timestamps.updatedAt),
          },
        })),
        total: data?.bills.page.total ?? undefined,
      };
    },
  },
);
